import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { Button } from "@mapmycustomers/ui";

import DownloadEntitiesButton from "@app/component/DownloadEntitiesModal/DownloadEntitiesButton";
import useSchedulerView from "@app/component/view/SchedulerView/utils/useSchedulerView";
import { schedulerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { getTimeframeStartForDate } from "@app/scene/activity/utils/navigation";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { parseApiDate } from "@app/util/parsers";

import { getSchedulerViewTotalFilteredRecordsCount } from "../../store";
import { exportSchedulerActivities } from "../../store/actions";

import NavigationButtons from "./NavigationButtons";
import styles from "./Toolbar.module.scss";
import { getTitle } from "./util/formatting";
import ViewModeButton from "./ViewModeButton";

interface Props {
  canDownload: boolean;
  exportActivities: typeof exportSchedulerActivities;
  totalFilteredRecordsCount: number;
}

const CalendarToolbar: React.FC<Props> = ({
  canDownload,
  exportActivities,
  totalFilteredRecordsCount,
}) => {
  const intl = useIntl();
  const { updateFilterModel, viewState } = useSchedulerView<Activity>();

  // We're pretty sure startAt is always a part of the filter
  // @ts-ignore
  const startAtDate = parseApiDate(viewState.filter.startAt.value);

  const handleGoToday = useCallback(() => {
    updateFilterModel({
      startAt: {
        operator: FilterOperator.ON,
        value: getTimeframeStartForDate(viewState.viewMode),
      },
    });
    schedulerViewAnalytics.clicked(["Today Button"]);
  }, [updateFilterModel, viewState.viewMode]);

  const handleDownload = useCallback(
    (columns: ColumnModel) => {
      exportActivities({ viewState: { ...viewState, columns } });
    },
    [exportActivities, viewState]
  );

  return (
    <div className={styles.container}>
      <div className={styles.bar}>
        <div className={styles.left}>
          <NavigationButtons timeframeStart={startAtDate} />
          <span className={styles.title}>{getTitle(intl, startAtDate, viewState.viewMode)}</span>
          <Button onClick={handleGoToday} type="default">
            {intl.formatMessage({
              id: "activities.schedulerView.calendar.toolbar.today",
              defaultMessage: "Today",
              description: "Today button title in Calendar view for Activities",
            })}
          </Button>
        </div>
        <div className={styles.right}>
          <ViewModeButton />
          {canDownload && (
            <DownloadEntitiesButton
              entityType={EntityType.ACTIVITY}
              hasAdditionalFilters
              informationPoint={intl.formatMessage({
                id: "activities.schedulerView.export.info",
                defaultMessage: "All activities in scheduler view",
                description: "Information for export on activity calendar",
              })}
              onDownload={handleDownload}
              total={totalFilteredRecordsCount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  totalFilteredRecordsCount: getSchedulerViewTotalFilteredRecordsCount(state),
});

const mapDispatchToProps = {
  exportActivities: exportSchedulerActivities,
};

export default connect(mapStateToProps, mapDispatchToProps)(CalendarToolbar);
